// What a section's column is made of: single tasks, and one entry standing in for each recurring series.
export type SectionEntry =
    | { kind: 'task', id: string }
    | { kind: 'series', id: string, tasks: any[] };

// The tasks of a section in board order, with each recurring series that has two or more tasks in the section
// collapsed into a single entry at the position of its first task. A task that isn't in `tasks` (say one just
// created and not yet in the list) simply stays an ordinary entry; a series with only one task here does too.
export function groupSectionEntries(taskIds: string[], tasks: any[]): SectionEntry[] {
    const tasksById = new Map<string, any>(tasks.map((task) => [task.id, task]));

    const seriesTasks = new Map<string, any[]>();
    for (const taskId of taskIds) {
        const task = tasksById.get(taskId);
        if (task?.seriesId) seriesTasks.set(task.seriesId, [...(seriesTasks.get(task.seriesId) ?? []), task]);
    }

    const seen = new Set<string>();
    const entries: SectionEntry[] = [];
    for (const taskId of taskIds) {
        const seriesId = tasksById.get(taskId)?.seriesId;
        const members = seriesId ? seriesTasks.get(seriesId)! : [];

        if (members.length < 2) {
            entries.push({ kind: 'task', id: taskId });
        } else if (!seen.has(seriesId)) {
            seen.add(seriesId);
            entries.push({ kind: 'series', id: seriesId, tasks: members });
        }
    }
    return entries;
}


// Dated tasks first, oldest to newest; undated ones last.
export const byDate = (a: any, b: any) => (a.date ?? '￿').localeCompare(b.date ?? '￿');

// The soonest occurrence that hasn't happened yet (`ordered` is in date order), or the last one when they are all behind us.
export const nextOccurrence = (ordered: any[], now: string) =>
    ordered.find((task) => task.date && task.date >= now) ?? ordered[ordered.length - 1];

// What a recurring series is called. Renaming "this and following" leaves the earlier occurrences with the old name, so the
// oldest occurrence's title is the wrong one to show; the next occurrence's is what the series is called now.
export function seriesName(tasks: any[], now: string): string {
    const ordered = [...tasks].sort(byDate);
    return nextOccurrence(ordered, now)?.title?.trim() || 'Untitled';
}
